import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { id: "modal-container-element" }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "search-container" }
const _hoisted_4 = ["value", "placeholder"]
const _hoisted_5 = ["rows", "total-items", "current-page", "loading"]

import { useStore } from 'vuex';
import { ref, onMounted, computed, watchEffect, watch } from 'vue';
import { EPINIO_TYPES } from '../types';
import Masthead from '@shell/components/ResourceList/Masthead';
import { attachActionMenu } from '../utils/table-formatters';
import { debounce } from 'lodash';
import { useNamespaces } from '../queries/useNamespaceQueries';
import { ListResourceRequestParams, ResourceQueryOptions } from '../models/resource/ui-types';
import { Namespace } from '../models/namespace/ui-types';
import NamespaceModal from '../components/namespace/NamespaceModal.vue';
import NamespaceDeleteModal from '../components/namespace/NamespaceDeleteModal.vue';
import Banner from '@components/Banner/Banner.vue';
import { ResourceTableRow } from '../models/resource/ui-types';
import { useUser } from '../queries/useUserQueries';


export default /*@__PURE__*/_defineComponent({
  __name: 'namespaces',
  props: {
    schema: {},
    rows: {}
  },
  setup(__props: any) {



const store = useStore() as any;
const t = store.getters['i18n/t'];

const resource: string = EPINIO_TYPES.NAMESPACE;

const namespaceModal = ref<InstanceType<typeof NamespaceModal> | null>(null);
const namespaceDeleteModal = ref<InstanceType<typeof NamespaceDeleteModal> | null>(null);

const { data: user, isError: isErrorUser, error: userError } = useUser(store);

const searchQuery = ref<string>('');

watch(searchQuery, (newQuery) => {
  onSearch(newQuery);
});

const requestParams = ref<ListResourceRequestParams>({
  page: 1,
  pageSize: 10,
  search: ''
});
const requestOptions = ref<ResourceQueryOptions>({ enabled: true, polling: true, isTablePagination: true });
const {data: namespaces, isLoading: isLoadingNamespaces, isError: isErrorNamespaces, error: namespacesError} = useNamespaces(store, requestParams, requestOptions);

const onSearch = debounce(async (query: string) => {
  requestParams.value.page = 1;
  requestParams.value.search = query;
}, 500);

// Strict RBAC: only show Create/Delete when the user has namespace write perms (admin).
// Defined ahead of the watchEffect that consumes them to avoid a TDZ on first run.
const canCreateNamespace = computed(() => {
  return user.value?.permissions?.namespace_create;
});
// Per-namespace delete is namespaced (server authorizes it against the role for
// the namespace being deleted), so it stays on the flat namespace_write and is
// further gated per-row by row.canDelete below.
const canDelete = computed(() => {
  return user.value?.permissions?.namespace_write || user.value?.permissions?.namespace;
});

const displayRows = computed<ResourceTableRow<Namespace>[]>(() => {
  if (!namespaces.value) {
    return [];
  }

  return (namespaces.value.items ?? []).map((n) => ({
    ...n,
    id: n.meta.name, // stable, unique per namespace
    availableActions: [{
      label: 'Delete',
      action: () => openDeleteModal(n),
      enabled: canDelete.value,
      visible: canDelete.value,
      danger: true,
    }]
  }));
});

// Watch for changes to the active namespace cache and update the request params accordingly
watchEffect(() => {
  void store.state.activeNamespaceCacheKey;
  const activeNamespaces = store.state.activeNamespaceCache;

  if (activeNamespaces && Object.keys(activeNamespaces).length > 0) {
    requestParams.value.namespaces = Object.keys(activeNamespaces);
  } else {
    requestParams.value.namespaces = undefined;
  }
});


onMounted(() => {
  // Opens the create namespace modal if the query is passed as query param
  if (store.$router.currentRoute._value.query.mode === 'openModal') {
    openCreateModal();
  }
});

async function openCreateModal() {
  namespaceModal.value?.openCreate();
}

function openDeleteModal(namespace: Namespace) {
  namespaceDeleteModal.value?.openDelete(namespace);
}

const columns = [
  {
    field: 'meta.name',
    label: t('epinio.namespace.name'),
  },
  {
    field: 'appCount',
    label: t('epinio.namespace.tableHeaders.appCount'),
    formatter: (_v: any, row: Namespace) => {
      return row.apps?.length || 0;
    }
  },
  {
    field: 'configCount',
    label: t('epinio.namespace.tableHeaders.configCount'),
    formatter: (_v: any, row: Namespace) => {
      return row.configurations?.length || 0;
    }
  },
  {
    field: 'meta.createdAt',
    label: t('epinio.namespace.tableHeaders.age'),
    formatter: 'age'
  }
];

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(Masthead), {
      schema: _ctx.schema,
      resource: _unref(resource)
    }, {
      createButton: _withCtx(() => [
        (canCreateNamespace.value)
          ? (_openBlock(), _createElementBlock("trailhand-button", {
              key: 0,
              variant: "primary",
              size: "large",
              onClick: openCreateModal
            }, _toDisplayString(_unref(t)('generic.create')), 1))
          : (_openBlock(), _createElementBlock("div", _hoisted_2))
      ]),
      _: 1
    }, 8, ["schema", "resource"]),
    _cache[2] || (_cache[2] = _createTextVNode()),
    (_unref(isErrorNamespaces))
      ? (_openBlock(), _createBlock(Banner, {
          key: 0,
          color: "error",
          label: _unref(namespacesError)?.message || _unref(t)('epinio.namespace.errors.fetch')
        }, null, 8, ["label"]))
      : _createCommentVNode("", true),
    _cache[3] || (_cache[3] = _createTextVNode()),
    (_unref(isErrorUser))
      ? (_openBlock(), _createBlock(Banner, {
          key: 1,
          color: "error",
          label: _unref(userError)?.message || _unref(t)('epinio.user.errors.fetch')
        }, null, 8, ["label"]))
      : _createCommentVNode("", true),
    _cache[4] || (_cache[4] = _createTextVNode()),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("trailhand-text-input", {
        value: searchQuery.value,
        placeholder: _unref(t)('generic.search'),
        onTextInputChange: _cache[0] || (_cache[0] = (e) => searchQuery.value = e.detail.value)
      }, null, 40, _hoisted_4)
    ]),
    _cache[5] || (_cache[5] = _createTextVNode()),
    _createElementVNode("trailhand-table", {
      ref: (el) => { if (el) el.renderActions = _unref(attachActionMenu); },
      rows: displayRows.value,
      columns: columns,
      "server-side": true,
      "total-items": _unref(namespaces)?.totalItems ?? 0,
      "current-page": requestParams.value.page,
      loading: _unref(isLoadingNamespaces),
      searchable: false,
      "key-field": "id",
      onPageChange: _cache[1] || (_cache[1] = (e) => { requestParams.value.page = e.detail.page; })
    }, null, 40, _hoisted_5),
    _cache[6] || (_cache[6] = _createTextVNode()),
    _createVNode(NamespaceModal, {
      ref_key: "namespaceModal",
      ref: namespaceModal
    }, null, 512),
    _cache[7] || (_cache[7] = _createTextVNode()),
    _createVNode(NamespaceDeleteModal, {
      ref_key: "namespaceDeleteModal",
      ref: namespaceDeleteModal
    }, null, 512)
  ]))
}
}

})